import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const Experience = ({ data }) => {
    return (
        <section id="experience" className="py-24 md:py-32 bg-background-alt relative">
            {/* Background accent */}
            <div className="absolute left-0 top-1/3 w-1/4 h-1/3 bg-primary/3 blur-[120px] pointer-events-none" />

            <div className="max-w-4xl mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="text-primary font-mono text-sm tracking-wider uppercase mb-3 block">
                        Career Path
                    </span>
                    <h2 className="text-3xl md:text-5xl font-display font-bold text-text tracking-tight">
                        Professional Experience
                    </h2>
                </motion.div>

                {/* Timeline */}
                <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-0 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-primary/50 via-border-light to-transparent" />

                    {data?.map((job, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="relative pl-8 md:pl-20 pb-12 last:pb-0 group"
                        >
                            {/* Timeline dot */}
                            <div className="absolute left-0 md:left-8 top-1 w-3 h-3 -translate-x-1/2 rounded-full bg-primary shadow-glow-sm group-hover:shadow-glow transition-shadow duration-300" />

                            {/* Card */}
                            <div className="p-6 rounded-2xl bg-surface border border-border hover:border-primary/20 hover:shadow-card-hover transition-all duration-500">
                                {/* Header */}
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                                    <div>
                                        <h3 className="text-lg font-bold text-text group-hover:text-primary transition-colors">
                                            {job.title}
                                        </h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-text-secondary text-sm">{job.company}</span>
                                            {job.type && (
                                                <span className="px-2 py-0.5 text-xs font-medium bg-secondary/10 text-secondary-light rounded-full border border-secondary/20">
                                                    {job.type}
                                                </span>
                                            )}
                                            {job.website && (
                                                <a
                                                    href={job.website}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-text-tertiary hover:text-primary transition-colors"
                                                    title={`Visit ${job.company}`}
                                                >
                                                    <ExternalLink size={12} />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-xs font-medium text-text-tertiary bg-surface-highlight px-3 py-1 rounded-full whitespace-nowrap border border-border">
                                        {job.years}
                                    </span>
                                </div>

                                {/* Description */}
                                <p className="text-text-tertiary text-sm mb-4 leading-relaxed">
                                    {job.description}
                                </p>

                                {/* Bullets */}
                                {job.bullets && job.bullets.length > 0 && (
                                    <ul className="space-y-2">
                                        {job.bullets.map((bullet, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-text-secondary leading-relaxed">
                                                <span className="mt-2 w-1 h-1 rounded-full bg-primary flex-shrink-0" />
                                                {bullet}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Experience;
